import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
    X,
    Save,
    Loader2,
    FileText,
    Package,
    Mail,
    Archive,
    RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { crud, api, downloadAdminFile } from "../lib/api";
import SearchableSelect from "./SearchableSelect";
import { fkId } from "../lib/fkId";
import { PANEL_MODAL_SHELL, PANEL_SECTION } from "../lib/panelFormStyles";
import { openSigortaMailCompose } from "../lib/sigortaMailto";

const DURUM_OPTS = [
    { value: "taslak", label: "Taslak" },
    { value: "hazir", label: "Gönderime Hazır" },
    { value: "gonderildi", label: "Gönderildi" },
    { value: "cevap_bekleniyor", label: "Cevap Bekleniyor" },
    { value: "tamamlandi", label: "Tamamlandı" },
];

const inputCls =
    "w-full bg-[#0B0F19] border border-white/10 rounded-md px-3 py-2 text-sm text-slate-100";

function applyPayloadToForm(data, setters) {
    if (!data) return;
    const {
        setKonu,
        setAlicilar,
        setSablonHtml,
        setDurum,
        setRecordId,
        setHasDocx,
        setHasZip,
        setPaketIcerik,
    } = setters;
    if (data.id != null) setRecordId(data.id);
    if (data.konu != null) setKonu(data.konu || data.mail_subject || "");
    if (data.alicilar != null || data.mail_to != null) {
        setAlicilar(data.alicilar ?? data.mail_to ?? "");
    }
    if (data.sablon_html != null || data.mail_body != null) {
        setSablonHtml(
            data.sablon_html ?? data.mail_body ?? data.mail_plain_text ?? "",
        );
    }
    if (data.durum) setDurum(data.durum);
    setHasDocx(Boolean(data.docx_url || data.olusturulan_docx));
    setHasZip(Boolean(data.zip_url || data.olusturulan_zip));
    if (Array.isArray(data.paket_icerik)) {
        setPaketIcerik(data.paket_icerik);
    }
}

export default function SigortaBasvuruFormModal({
    open,
    title,
    initial,
    onClose,
    onSaved,
}) {
    const isEdit = !!initial?.id;
    const [recordId, setRecordId] = useState(initial?.id ?? null);
    const [sigortaDosyasi, setSigortaDosyasi] = useState(
        () => fkId(initial?.sigorta_dosyasi) ?? null,
    );
    const [sigortaSirketi, setSigortaSirketi] = useState(
        () => fkId(initial?.sigorta_sirketi) ?? null,
    );
    const [kusurluPlaka, setKusurluPlaka] = useState(
        initial?.kusurlu_arac_plaka ?? "",
    );
    const [konu, setKonu] = useState(initial?.konu ?? "");
    const [alicilar, setAlicilar] = useState(initial?.alicilar ?? "");
    const [sablonHtml, setSablonHtml] = useState(initial?.sablon_html ?? "");
    const [durum, setDurum] = useState(initial?.durum ?? "taslak");
    const [hasDocx, setHasDocx] = useState(Boolean(initial?.olusturulan_docx));
    const [hasZip, setHasZip] = useState(Boolean(initial?.olusturulan_zip));
    const [paketIcerik, setPaketIcerik] = useState([]);
    const [busy, setBusy] = useState(false);
    const [actionBusy, setActionBusy] = useState(null);
    const [err, setErr] = useState(null);

    const setters = {
        setKonu,
        setAlicilar,
        setSablonHtml,
        setDurum,
        setRecordId,
        setHasDocx,
        setHasZip,
        setPaketIcerik,
    };

    const loadRecord = useCallback(async (id) => {
        if (!id) return;
        try {
            const data = await crud.retrieve("sigorta-basvurulari", id);
            applyPayloadToForm(data, setters);
        } catch {
            /* sessiz */
        }
    }, []);

    useEffect(() => {
        if (!open) return;
        setErr(null);
        setRecordId(initial?.id ?? null);
        setSigortaDosyasi(fkId(initial?.sigorta_dosyasi) ?? null);
        setSigortaSirketi(fkId(initial?.sigorta_sirketi) ?? null);
        setKusurluPlaka(initial?.kusurlu_arac_plaka ?? "");
        setKonu(initial?.konu ?? "");
        setAlicilar(initial?.alicilar ?? "");
        setSablonHtml(initial?.sablon_html ?? "");
        setDurum(initial?.durum ?? "taslak");
        setHasDocx(Boolean(initial?.olusturulan_docx));
        setHasZip(Boolean(initial?.olusturulan_zip));
        setPaketIcerik([]);
        if (initial?.id) loadRecord(initial.id);
    }, [open, initial, loadRecord]);

    const fetchSirketAlicilar = useCallback(async (sirketId) => {
        if (!sirketId) return "";
        try {
            const r = await api.get(
                `/v1/admin/sigorta-sirketleri/${sirketId}/muracaat-emailleri/`,
            );
            const line = r.data?.alicilar || "";
            if (line) {
                setAlicilar(line);
                return line;
            }
            toast.warning(
                "Bu şirket için kayıtlı e-posta yok. Sigorta şirketleri ekranından e-posta ekleyin.",
            );
            return "";
        } catch (e) {
            toast.error(
                e?.response?.data?.detail ||
                    e?.message ||
                    "Şirket e-postaları yüklenemedi.",
            );
            return "";
        }
    }, []);

    useEffect(() => {
        if (!open || !sigortaSirketi) return;
        fetchSirketAlicilar(sigortaSirketi);
    }, [open, sigortaSirketi, fetchSirketAlicilar]);

    async function saveRecord(extra = {}) {
        if (!sigortaDosyasi) {
            setErr("Sigorta dosyası seçiniz.");
            return null;
        }
        if (!sigortaSirketi) {
            setErr("Sigorta şirketi seçiniz.");
            return null;
        }
        setBusy(true);
        setErr(null);
        const body = {
            sigorta_dosyasi: sigortaDosyasi,
            sigorta_sirketi: sigortaSirketi,
            kusurlu_arac_plaka: kusurluPlaka || "",
            konu: konu || "",
            sablon_html: sablonHtml || "",
            durum,
            refresh_mail: "1",
            ...extra,
        };
        if ((alicilar || "").trim()) {
            body.alicilar = alicilar.trim();
        }
        try {
            let data;
            if (recordId) {
                data = await crud.update("sigorta-basvurulari", recordId, body);
            } else {
                const res = await api.post("/v1/admin/sigorta-basvurulari/", body);
                data = res.data;
            }
            applyPayloadToForm(data, setters);
            if (data?.id) setRecordId(data.id);
            if (data?.alici_uyari) toast.warning(data.alici_uyari);
            return data;
        } catch (e) {
            const d = e?.response?.data;
            setErr(
                d?.detail ||
                    (typeof d === "object" ? JSON.stringify(d) : null) ||
                    e?.message ||
                    "Kayıt başarısız.",
            );
            return null;
        } finally {
            setBusy(false);
        }
    }

    async function ensureId() {
        if (recordId) return recordId;
        const saved = await saveRecord();
        return saved?.id ?? null;
    }

    async function runAction(actionName, successMsg) {
        const id = await ensureId();
        if (!id) return;
        setActionBusy(actionName);
        try {
            const data = await crud.action(
                "sigorta-basvurulari",
                id,
                actionName,
            );
            applyPayloadToForm(data, setters);
            toast.success(successMsg);
            onSaved?.();
        } catch (e) {
            toast.error(
                e?.response?.data?.detail ||
                    e?.message ||
                    "İşlem başarısız.",
            );
        } finally {
            setActionBusy(null);
        }
    }

    async function handleSave() {
        const data = await saveRecord();
        if (!data) return;
        toast.success(
            isEdit
                ? "Başvuru güncellendi."
                : "Başvuru oluşturuldu — dilekçe ve ZIP paketi hazırlandı.",
        );
        onSaved?.();
        if (!isEdit && data?.id) return;
        onClose();
    }

    async function handleMailHazirla() {
        const id = await ensureId();
        if (!id) return;
        setActionBusy("mail");
        try {
            const data = await crud.action(
                "sigorta-basvurulari",
                id,
                "mail_hazirla",
            );
            applyPayloadToForm(data, setters);
            openSigortaMailCompose(
                { konu: data.konu, alicilar: data.alicilar },
                {
                    alicilar: data.alicilar,
                    konu: data.konu,
                    mail_plain_text: data.mail_body ?? data.sablon_html,
                },
            );
            toast.success("Mail taslağı hazır — alıcı, konu ve metin e-posta programına aktarıldı.");
        } catch (e) {
            toast.error(
                e?.response?.data?.detail ||
                    e?.message ||
                    "Mail hazırlanamadı.",
            );
        } finally {
            setActionBusy(null);
        }
    }

    if (!open) return null;

    const fileLabel = initial?.sigorta_dosyasi_label || recordId || "yeni";
    const showToolbar = Boolean(sigortaDosyasi && sigortaSirketi);

    const modal = (
        <div
            className="fixed inset-0 z-[80] overflow-y-auto"
            role="dialog"
            aria-modal="true"
        >
            <div
                className="fixed inset-0 bg-black/75 backdrop-blur-sm"
                onClick={onClose}
                aria-hidden="true"
            />
            <div className="relative min-h-full flex items-start justify-center px-4 py-8">
                <div
                    className={`${PANEL_MODAL_SHELL} max-w-3xl w-full`}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 shrink-0">
                        <h2 className="text-lg font-semibold text-slate-100">
                            {title}
                        </h2>
                        <button
                            type="button"
                            onClick={onClose}
                            className="h-8 w-8 rounded-md hover:bg-white/10 flex items-center justify-center text-slate-400"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>

                    <div className="overflow-y-auto flex-1 p-5 space-y-4">
                        {err && (
                            <div className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-md px-3 py-2">
                                {err}
                            </div>
                        )}

                        <div className={PANEL_SECTION + " p-3"}>
                            <label className="block text-xs text-slate-500 mb-1">
                                Sigorta dosyası *
                            </label>
                            <SearchableSelect
                                value={sigortaDosyasi}
                                onChange={setSigortaDosyasi}
                                resource="sigorta-dosyalari"
                                placeholder="Dosya seçin..."
                            />
                        </div>

                        <div className={PANEL_SECTION + " p-3"}>
                            <label className="block text-xs text-slate-500 mb-1">
                                Sigorta şirketi *
                            </label>
                            <SearchableSelect
                                value={sigortaSirketi}
                                onChange={setSigortaSirketi}
                                resource="sigorta-sirketleri"
                                placeholder="Şirket seçin..."
                            />
                        </div>

                        <div className={PANEL_SECTION + " p-3"}>
                            <label className="block text-xs text-slate-500 mb-1">
                                Kusurlu araç plaka
                            </label>
                            <input
                                value={kusurluPlaka}
                                onChange={(e) => setKusurluPlaka(e.target.value)}
                                className={inputCls}
                                placeholder="34 XX 0000"
                            />
                        </div>

                        {showToolbar && (
                            <div className="flex flex-wrap gap-2 p-3 rounded-lg border border-[#D4AF37]/25 bg-[#D4AF37]/8">
                                <p className="w-full text-[11px] text-slate-500">
                                    Dilekçe DOCX oluşturulur ve ZIP paketine
                                    eklenir.
                                </p>
                                <ToolbarBtn
                                    label="Dilekçe Oluştur"
                                    icon={FileText}
                                    busy={actionBusy === "dilekce_olustur"}
                                    onClick={() =>
                                        runAction(
                                            "dilekce_olustur",
                                            "Dilekçe oluşturuldu.",
                                        )
                                    }
                                    cls="bg-emerald-500/15 border-emerald-500/30 text-emerald-300"
                                />
                                <ToolbarBtn
                                    label="Paket Hazırla"
                                    icon={Package}
                                    busy={actionBusy === "hazirla_paket"}
                                    onClick={() =>
                                        runAction(
                                            "hazirla_paket",
                                            "Paket hazır (dilekçe + evraklar).",
                                        )
                                    }
                                    cls="bg-[#D4AF37]/15 border-[#D4AF37]/30 text-[#F3E5AB]"
                                />
                                <ToolbarBtn
                                    label="Mail Hazırla"
                                    icon={Mail}
                                    busy={actionBusy === "mail"}
                                    onClick={handleMailHazirla}
                                    cls="bg-sky-500/15 border-sky-500/30 text-sky-300"
                                />
                                {hasDocx && recordId && (
                                    <ToolbarBtn
                                        label="DOCX İndir"
                                        icon={FileText}
                                        onClick={() =>
                                            downloadAdminFile(
                                                "sigorta-basvurulari",
                                                [recordId, "indir_docx"],
                                                `Dilekce_${fileLabel}.docx`,
                                            )
                                        }
                                        cls="border-white/10 text-slate-300"
                                    />
                                )}
                                {hasZip && recordId && (
                                    <ToolbarBtn
                                        label="ZIP İndir"
                                        icon={Archive}
                                        onClick={() =>
                                            downloadAdminFile(
                                                "sigorta-basvurulari",
                                                [recordId, "indir_zip"],
                                                `Paket_${fileLabel}.zip`,
                                            )
                                        }
                                        cls="border-white/10 text-slate-300"
                                    />
                                )}
                                <ToolbarBtn
                                    label="Alıcıları yenile"
                                    icon={RefreshCw}
                                    onClick={() =>
                                        fetchSirketAlicilar(sigortaSirketi)
                                    }
                                    cls="border-white/10 text-slate-400"
                                />
                            </div>
                        )}

                        {paketIcerik.length > 0 && (
                            <div className="text-[11px] text-slate-400 border border-white/10 rounded-md px-3 py-2">
                                <span className="font-semibold text-slate-500">
                                    Paket içeriği:{" "}
                                </span>
                                {paketIcerik.join(" · ")}
                            </div>
                        )}

                        <div className={PANEL_SECTION + " p-3"}>
                            <label className="block text-xs text-slate-500 mb-1">
                                Kime (alıcılar)
                            </label>
                            <textarea
                                value={alicilar}
                                onChange={(e) => setAlicilar(e.target.value)}
                                rows={2}
                                className={`${inputCls} font-mono-jb`}
                                placeholder="sirket@sigorta.com"
                            />
                        </div>

                        <div className={PANEL_SECTION + " p-3"}>
                            <label className="block text-xs text-slate-500 mb-1">
                                Konu
                            </label>
                            <input
                                value={konu}
                                onChange={(e) => setKonu(e.target.value)}
                                className={inputCls}
                            />
                        </div>

                        <div className={PANEL_SECTION + " p-3"}>
                            <label className="block text-xs text-slate-500 mb-1">
                                Mail içeriği
                            </label>
                            <textarea
                                value={sablonHtml}
                                onChange={(e) => setSablonHtml(e.target.value)}
                                rows={10}
                                className={`${inputCls} font-mono-jb`}
                            />
                        </div>

                        <div className={PANEL_SECTION + " p-3"}>
                            <label className="block text-xs text-slate-500 mb-1">
                                Durum
                            </label>
                            <select
                                value={durum}
                                onChange={(e) => setDurum(e.target.value)}
                                className={inputCls}
                            >
                                {DURUM_OPTS.map((o) => (
                                    <option key={o.value} value={o.value}>
                                        {o.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="flex justify-end gap-2 px-5 py-4 border-t border-white/10 shrink-0">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm text-slate-400 hover:text-white"
                        >
                            Kapat
                        </button>
                        <button
                            type="button"
                            disabled={busy}
                            onClick={handleSave}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#D4AF37] text-[#0B0F19] text-sm font-semibold disabled:opacity-50"
                        >
                            {busy ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <Save className="h-4 w-4" />
                            )}
                            Kaydet
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    return createPortal(modal, document.body);
}

function ToolbarBtn({ label, icon: Icon, onClick, busy, disabled, cls = "" }) {
    return (
        <button
            type="button"
            disabled={disabled || busy}
            onClick={onClick}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border disabled:opacity-50 ${cls}`}
        >
            {busy ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
                <Icon className="h-3.5 w-3.5" />
            )}
            {label}
        </button>
    );
}
